import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { LatteButton } from '../common';

const OnboardingTour = ({ 
  isActive, 
  onComplete, 
  onSkip,
  steps = [],
  currentStepIndex = 0,
  onStepChange,
  onVoiceTestActivate,
  onVoiceTestDeactivate,
  onResetVoiceTest,
  isVoiceTestCompleted = false,
  showVoiceTestGuide = false
}) => {
  const [activeStep, setActiveStep] = useState(currentStepIndex);
  const [isVisible, setIsVisible] = useState(false);
  const [forceRender, setForceRender] = useState(0); // 강제 재렌더링을 위한 state
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const lastVoiceTestStateRef = useRef(null);
  const voiceTestTooltipPositionRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      setActiveStep(currentStepIndex);
      // 페이지 스크롤 방지
      document.body.style.overflow = 'hidden';
      
      const checkForTarget = () => {
        const currentStepData = steps[currentStepIndex];
        if (currentStepData?.target) {
          const selectors = currentStepData.target.split(',').map(s => s.trim());
          const targetFound = selectors.some(selector => {
            const elements = document.querySelectorAll(selector);
            return elements.length > 0;
          });
          
          if (!targetFound) {
            setTimeout(checkForTarget, 200);
          }
        }
      };
      
      checkForTarget();
    } else {
      setIsVisible(false);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isActive, currentStepIndex, steps]);

  useEffect(() => {
    if (onStepChange) {
      onStepChange(activeStep);
    }
  }, [activeStep, onStepChange]);

  useEffect(() => {
    const currentStepData = steps[activeStep];
    const isVoiceTestRequired = currentStepData?.enableVoiceTest;
    
    if (lastVoiceTestStateRef.current !== isVoiceTestRequired) {
      if (isVoiceTestRequired && !lastVoiceTestStateRef.current) {
        if (onVoiceTestActivate) {
          onVoiceTestActivate();
        }
      } else if (!isVoiceTestRequired && lastVoiceTestStateRef.current) {
        if (onVoiceTestDeactivate) {
          onVoiceTestDeactivate();
        }
      }
      
      lastVoiceTestStateRef.current = isVoiceTestRequired;
    }
  }, [activeStep, steps, onVoiceTestActivate, onVoiceTestDeactivate]);

  const currentStep = steps[activeStep];

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      const nextStep = activeStep - 1;
      const nextStepData = steps[nextStep];
      
      // 이전 단계가 음성 테스트 단계인 경우 음성 테스트 완전 초기화
      if (nextStepData?.enableVoiceTest && onResetVoiceTest) {
        console.log('🔄 이전 단계가 음성 테스트 단계 - 완전 초기화 실행');
        onResetVoiceTest();
      }
      
      setActiveStep(nextStep);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (onVoiceTestDeactivate) {
      onVoiceTestDeactivate();
    }
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    if (onVoiceTestDeactivate) {
      onVoiceTestDeactivate();
    }
    if (onSkip) {
      onSkip();
    }
  };

  const getTargetElement = () => {
    if (!currentStep?.target) return null;
    
    const selectors = currentStep.target.split(',').map(s => s.trim());
    
    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      
      if (elements.length > 0) {
        return elements[0];
      }
    }
    
    return null;
  };

  const calculateTooltipPosition = () => {
    const targetElement = getTargetElement();
    if (!targetElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 280;
    const margin = 20;
    const minDistanceFromEdge = 60;

    let top, left, transform = '';

    if (currentStep?.enableVoiceTest) {
      const targetElement = getTargetElement();
      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect();
        top = targetRect.bottom + margin;
        left = targetRect.right + margin;
        
        if (left + tooltipWidth > window.innerWidth - minDistanceFromEdge) {
          left = targetRect.left - tooltipWidth - margin;
        }
        
        if (top + tooltipHeight > window.innerHeight - minDistanceFromEdge) {
          top = targetRect.top - tooltipHeight - margin;
        }
        
        if (left < minDistanceFromEdge) {
          left = minDistanceFromEdge;
        }
        if (top < minDistanceFromEdge) {
          top = minDistanceFromEdge;
        }
      } else {
        top = window.innerHeight / 2 - tooltipHeight / 2;
        left = window.innerWidth / 2 + 100;
      }
      
      return { top: `${top}px`, left: `${left}px` };
    }


    if (rect.right + tooltipWidth + margin < window.innerWidth - minDistanceFromEdge) {
      left = rect.right + margin;
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
    }
    else if (rect.left - tooltipWidth - margin > minDistanceFromEdge) {
      left = rect.left - tooltipWidth - margin;
      top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
    }
    else if (rect.top - tooltipHeight - margin > minDistanceFromEdge) {
      top = rect.top - tooltipHeight - margin;
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    }
    else {
      top = rect.bottom + margin;
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      
      if (top + tooltipHeight > window.innerHeight - minDistanceFromEdge) {
        top = minDistanceFromEdge;
      }
    }

    // 좌우 경계 조정
    if (left < minDistanceFromEdge) {
      left = minDistanceFromEdge;
    }
    if (left + tooltipWidth > window.innerWidth - minDistanceFromEdge) {
      left = window.innerWidth - tooltipWidth - minDistanceFromEdge;
    }

    // 상하 경계 조정
    if (top < minDistanceFromEdge) {
      top = minDistanceFromEdge;
    }
    if (top + tooltipHeight > window.innerHeight - minDistanceFromEdge) {
      top = window.innerHeight - tooltipHeight - minDistanceFromEdge;
    }

    return { top: `${top}px`, left: `${left}px` };
  };

  // 음성 테스트 단계의 하이라이트 영역을 저장하는 ref
  const voiceTestHighlightRef = useRef(null);
  
  // 음성 테스트 단계가 시작될 때 하이라이트 영역과 툴팁 위치 저장
  useEffect(() => {
    const currentStepData = steps[activeStep];
    if (currentStepData?.enableVoiceTest) {
      const targetElement = getTargetElement();
      if (targetElement) {
        // 음성 테스트 단계에서는 타겟 요소의 padding을 고려해야 함
        const rect = targetElement.getBoundingClientRect();
        const padding = 8;
        
        // 음성 테스트 가이드 박스가 포함되도록 상단에 추가 공간 확보
        const guideBoxHeight = 90; // 가이드 박스의 실제 높이 (중앙 정렬을 위해 약간 늘림)
        
        // 음성 테스트 완료 후 확장할 경우를 고려
        if (isVoiceTestCompleted && currentStepData.expandOnComplete) {
          // 채팅 영역 전체로 확장
          const chatAreaElement = document.querySelector('[data-tour="chat-area"]');
          const chatInputElement = document.querySelector('[data-tour="chat-input"]');
          if (chatAreaElement && chatInputElement) {
            const chatRect = chatAreaElement.getBoundingClientRect();
            const inputRect = chatInputElement.getBoundingClientRect();
            voiceTestHighlightRef.current = {
              top: chatRect.top - padding,
              left: chatRect.left - padding,
              width: chatRect.width + padding * 2,
              height: chatRect.height + inputRect.height + padding * 2
            };
          }
        } else {
          voiceTestHighlightRef.current = {
            top: rect.top - padding - guideBoxHeight, // 가이드 높이만큼 위로 확장
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2 + guideBoxHeight
          };
        }
        
        // 음성 테스트 시작 시 툴팁 위치도 함께 저장
        if (!voiceTestTooltipPositionRef.current) {
          voiceTestTooltipPositionRef.current = calculateTooltipPosition();
        }
      }
    } else if (!currentStepData?.enableVoiceTest) {
      // 음성 테스트가 아닌 단계로 이동하면 저장된 영역 초기화
      voiceTestHighlightRef.current = null;
      voiceTestTooltipPositionRef.current = null;
    }
    // 음성 테스트 완료 시 강제 재렌더링
    setForceRender(prev => prev + 1);
  }, [activeStep, steps, isVoiceTestCompleted]);
  
  const getHighlightStyle = () => {
    const targetElement = getTargetElement();
    if (!targetElement) {
      return {};
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8;

    return {
      top: `${rect.top - padding}px`,
      left: `${rect.left - padding}px`,
      width: `${rect.width + padding * 2}px`,
      height: `${rect.height + padding * 2}px`
    };
  };

  if (!isVisible || !currentStep) {
    return null;
  }

  const isVoiceTestStep = currentStep?.enableVoiceTest;

  return (
    <div className="fixed inset-0" style={{ zIndex: isVoiceTestStep ? 50 : 9997 }}>
      {getTargetElement() && (() => {
        const targetElement = getTargetElement();
        const rect = targetElement.getBoundingClientRect();
        const padding = 8;
        
        const highlightTop = rect.top - padding;
        const highlightLeft = rect.left - padding;
        const highlightWidth = rect.width + padding * 2;
        const highlightHeight = rect.height + padding * 2;
        
        const isVoiceTestStep = currentStep?.enableVoiceTest;
        
        let extendedHighlightHeight = highlightHeight;
        let extendedHighlightTop = highlightTop;
        let extendedHighlightLeft = highlightLeft;
        let extendedHighlightWidth = highlightWidth;
        
        // 음성 테스트 단계에서는 저장된 하이라이트 영역 사용
        if (isVoiceTestStep && voiceTestHighlightRef.current) {
          extendedHighlightWidth = voiceTestHighlightRef.current.width;
          extendedHighlightHeight = voiceTestHighlightRef.current.height;
          extendedHighlightLeft = voiceTestHighlightRef.current.left;
          extendedHighlightTop = voiceTestHighlightRef.current.top;
        } else if (isVoiceTestStep) {
          // 저장된 영역이 없으면 현재 타겟 요소를 기준으로 계산
          const rect = targetElement.getBoundingClientRect();
          const guideBoxHeight = 90; // 음성 테스트 가이드 박스의 실제 높이
          
          // 음성 테스트 완료 후 확장할 경우를 고려
          if (isVoiceTestCompleted && currentStep.expandOnComplete) {
            // 채팅 영역 전체로 확장
            const chatAreaElement = document.querySelector('[data-tour="chat-area"]');
            const chatInputElement = document.querySelector('[data-tour="chat-input"]');
            if (chatAreaElement && chatInputElement) {
              const chatRect = chatAreaElement.getBoundingClientRect();
              const inputRect = chatInputElement.getBoundingClientRect();
              extendedHighlightWidth = chatRect.width + padding * 2;
              extendedHighlightHeight = chatRect.height + inputRect.height + padding * 2;
              extendedHighlightLeft = chatRect.left - padding;
              extendedHighlightTop = chatRect.top - padding;
            }
          } else {
            extendedHighlightWidth = rect.width + padding * 2;
            extendedHighlightHeight = rect.height + padding * 2 + guideBoxHeight;
            extendedHighlightLeft = rect.left - padding;
            extendedHighlightTop = rect.top - padding - guideBoxHeight; // 가이드 높이만큼 위로
          }
          
          // 영역 저장
          voiceTestHighlightRef.current = {
            top: extendedHighlightTop,
            left: extendedHighlightLeft,
            width: extendedHighlightWidth,
            height: extendedHighlightHeight
          };
        }
        
        const shouldShowOverlay = true;
        const overlayZIndex = isVoiceTestStep ? 50 : 9998;
        const highlightZIndex = isVoiceTestStep ? 55 : 9999;
        
        return (
          <>
            {shouldShowOverlay && (
              <>
                {/* 상단 오버레이 */}
                <div 
                  className="onboarding-overlay"
                  style={{
                    top: 0,
                    left: 0,
                    right: 0,
                    height: `${Math.max(0, extendedHighlightTop)}px`,
                    zIndex: overlayZIndex
                  }}
                />
                {/* 하단 오버레이 */}
                <div 
                  className="onboarding-overlay"
                  style={{
                    top: `${extendedHighlightTop + extendedHighlightHeight}px`,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: overlayZIndex
                  }}
                />
                {/* 좌측 오버레이 */}
                <div 
                  className="onboarding-overlay"
                  style={{
                    top: `${extendedHighlightTop}px`,
                    left: 0,
                    width: `${Math.max(0, extendedHighlightLeft)}px`,
                    height: `${extendedHighlightHeight}px`,
                    zIndex: overlayZIndex
                  }}
                />
                {/* 우측 오버레이 */}
                <div 
                  className="onboarding-overlay"
                  style={{
                    top: `${extendedHighlightTop}px`,
                    left: `${extendedHighlightLeft + extendedHighlightWidth}px`,
                    right: 0,
                    height: `${extendedHighlightHeight}px`,
                    zIndex: overlayZIndex
                  }}
                />
              </>
            )}
            
            {/* 하이라이트 테두리 */}
            <div
              className="onboarding-highlight"
              style={{
                top: `${extendedHighlightTop}px`,
                left: `${extendedHighlightLeft}px`,
                width: `${extendedHighlightWidth}px`,
                height: `${extendedHighlightHeight}px`,
                zIndex: highlightZIndex
              }}
            />
          </>
        );
      })()}

      {/* 툴팁 */}
      <div
        ref={tooltipRef}
        className="onboarding-tooltip"
        style={{
          ...(isVoiceTestStep && voiceTestTooltipPositionRef.current 
            ? voiceTestTooltipPositionRef.current 
            : calculateTooltipPosition()),
          zIndex: isVoiceTestStep ? 120 : 10000
        }}
      >
        {/* 헤더 */}
        <div className="onboarding-header">
          <div className="flex-between">
            <div className="flex gap-3">
              <div className="onboarding-step-circle flex-center">
                {activeStep + 1}
              </div>
              <span className="text-gray-500 text-sm">
                {activeStep + 1} / {steps.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="p-1 rounded-full transition-colors hover:bg-gray-100"
              title="건너뛰기"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <h3 className="onboarding-title">
            {currentStep.title}
          </h3>
        </div>

        {/* 컨텐츠 영역 (스크롤 가능) */}
        <div className="onboarding-content">
          <p className="onboarding-description">
            {currentStep.description}
          </p>

  
          {currentStep.tip && (
            <div className="onboarding-tip">
              <p className="onboarding-tip-text">
                💡 <strong>팁:</strong> {currentStep.tip}
              </p>
            </div>
          )}
        </div>


        <div className="onboarding-footer">
          <div className="onboarding-footer-actions">
            <div className="flex gap-2">
              {activeStep > 0 && (
                <LatteButton
                  onClick={handlePrevious}
                  variant="secondary"
                  size="sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  이전
                </LatteButton>
              )}
            </div>

            <div className="flex gap-2">
              <LatteButton
                onClick={handleSkip}
                variant="outline"
                size="sm"
              >
                건너뛰기
              </LatteButton>
              <LatteButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext();
                }}
                size="md"
                className="flex-shrink-0"
              >
                {activeStep === steps.length - 1 ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    완료
                  </>
                ) : (
                  <>
                    다음
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </LatteButton>
            </div>
          </div>

          
          <div className="onboarding-progress">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`onboarding-progress-bar ${
                  index <= activeStep ? 'active' : 'inactive'
                }`}
              />
            ))}
          </div>
        </div>
      </div>



    </div>
  );
};

export default OnboardingTour;
